import React, { createContext, ReactNode, useContext } from 'react';
import { UseTRPCQueryResult } from '@trpc/react-query/shared';
import { AppRouter } from 'backend/src/api/trpc/app-router';
import { TRPCClientErrorLike } from '@trpc/client';
import { LineItemApiListResponse } from 'shared/src/api/line-item-api-types';
import {
  ProposalPromoteDiffApiResponse,
  StrategyApiResponse
} from 'shared/src/api/strategy-api-types';
import { isReady } from 'shared/src/utils/zod/trpc-zod-utils';
import { StrategyTrpcResponse } from 'shared/src/trpc-types/strategy-trpc-types';
import { LineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';

type ContextValue = {
  proposal: StrategyTrpcResponse;
  lineItems: LineItemTrpcResponse[];
  diff: ProposalPromoteDiffApiResponse;
};

const ProposalContext = createContext<ContextValue | null>(null);

type Props = {
  proposal: UseTRPCQueryResult<StrategyApiResponse, TRPCClientErrorLike<AppRouter>>;
  lineItems: UseTRPCQueryResult<LineItemApiListResponse, TRPCClientErrorLike<AppRouter>>;
  diff: UseTRPCQueryResult<ProposalPromoteDiffApiResponse, TRPCClientErrorLike<AppRouter>>;
  children: ReactNode;
};

// handle waiting for data to load before rendering
export function ProposalContextProvider({ proposal, lineItems, diff, children }: Props) {
  if (proposal.isError || lineItems.isError || diff.isError) {
    return <div>Error loading proposal data.</div>;
  }

  if (!isReady(proposal) || !isReady(lineItems) || !isReady(diff)) {
    return <div>Loading...</div>;
  }

  return (
    <ProposalContext
      value={{
        proposal: StrategyTrpcResponse.parse(proposal.data),
        lineItems: lineItems.data.data.map(li => LineItemTrpcResponse.parse(li)),
        diff: diff.data
      }}>
      {children}
    </ProposalContext>
  );
}

export function useProposalContext() {
  const value = useContext(ProposalContext);
  if (!value) {
    throw new Error('useProposalContext must be used within a ProposalContextProvider');
  }
  return value;
}
